import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";

import { filter, map } from "rxjs";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import {
  LektoAuthService,
  NotificationsPanelContainerComponent,
  NotificationsStateService,
  NotificationsToasterContainerComponent,
  OfflineModeBannerComponent,
  OfflineModeStateService,
  OfflineSyncToastComponent,
  ProfileConstants
} from "@frontends-v3/shared-core";

import { CurrentUserStateService } from "../../../core/services/current-user-state.service";
import { PageHeaderStateService } from "../../../core/services/page-header-state.service";
import { MenuComponent } from "../menu/menu.component";
import { BottomNavComponent } from "../bottom-nav/bottom-nav.component";
import { PlatformAssistantComponent } from "../../../features/assistant/platform-assistant.component";

@Component({
  selector: "app-navbar-component",
  standalone: true,
  imports: [
    NzLayoutModule,
    MenuComponent,
    RouterOutlet,
    BottomNavComponent,
    NotificationsPanelContainerComponent,
    NotificationsToasterContainerComponent,
    PlatformAssistantComponent,
    OfflineModeBannerComponent,
    OfflineSyncToastComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./navbar.html",
  styleUrl: "./navbar.scss"
})
export class AppNavBarComponent {
  private readonly currentUserStateService = inject(CurrentUserStateService);
  private readonly pageHeaderState = inject(PageHeaderStateService);
  private readonly router = inject(Router);
  public readonly notificationsState = inject(NotificationsStateService);
  public readonly offlineState = inject(OfflineModeStateService);
  private readonly authService = inject(LektoAuthService);
  /** DEV-1104 (R1): o Assistente Pedagógico não existe para o leitor — o chat recusa o papel dele. */
  public readonly isReader = computed<boolean>(() =>
    ProfileConstants.isReaderProfile(this.authService.authProfile())
  );
  public readonly isCollapsed = signal(false);
  private readonly closedByButton = signal(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  public readonly userName = this.currentUserStateService.userName;

  public readonly icon = computed<string>(() => {
    const url = this.currentUrl();
    if (url.startsWith("/library")) return "uil uil-book-alt";
    if (url.startsWith("/class")) return "uil uil-users-alt";
    return "uil uil-estate";
  });

  public readonly pageTitle = computed<string>(() => {
    const url = this.currentUrl();
    if (url.startsWith("/library")) return "Biblioteca";
    if (url.startsWith("/class")) return "Suas Turmas";
    // DEV-910 CA05: na tela de atribuições, a aba de escopo ativa (Minhas/domínio do gestor) pode
    // substituir o título/subtítulo padrão sem trocar de rota — ver PageHeaderStateService.
    if (url.startsWith("/action"))
      return this.pageHeaderState.current()?.title ?? "Suas atribuições";
    if (url.startsWith("/my-account")) return "Minha Conta";
    return `Olá, ${this.userName()}!`;
  });

  public readonly pageDescription = computed<string>(() => {
    const url = this.currentUrl();
    if (url.startsWith("/library"))
      return "Todo o conteúdo da plataforma reunido num só lugar. Comece pelas recomendações pensadas para o seu perfil e explore o acervo completo com busca e filtros.";

    if (url.startsWith("/class")) return "Explore as Turmas que você está associado.";

    if (url.startsWith("/action")) {
      return (
        this.pageHeaderState.current()?.description ??
        "As trilhas que você está conduzindo com suas turmas, do primeiro dia ao fechamento."
      );
    }

    if (url.startsWith("/my-account")) return "Administre sua conta";

    return "Conteúdos recomendados para você";
  });

  // Telas de detalhe (library/class/action/manage-assignment + id) rente às bordas
  // (padding/gap zero): cada tela cuida do próprio espaçamento. Sem manage-assignment aqui,
  // o padding do shell somaria com o detail-container da própria página.
  public readonly flushBody = computed<boolean>(() => {
    const url = this.currentUrl().split("?")[0];
    return /^\/(library|class|action|manage-assignment)\/[^/]+/.test(url);
  });

  public readonly showPageHeader = computed<boolean>(() => {
    const url = this.currentUrl().split("?")[0];
    // CA01 (DEV-833): a home renderiza a própria saudação "Olá, [Nome]!" no padrão
    // da nova DS — o hero do navbar fica desligado na rota raiz.
    if (url === "/" || url === "") return false;
    // O hero de página é único e mora aqui. DEV-816 (/library) e DEV-795 (/action) haviam
    // desligado a faixa nessas rotas para cada tela renderizar um banner próprio; a decisão
    // foi revertida — os banners locais estão desativados e o hero volta a valer para todas
    // as listagens, com título/descrição vindos de pageTitle()/pageDescription().
    // startsWith e não igualdade: a rota agora carrega o :idAssignment.
    if (url.startsWith("/manage-assignment")) return false;
    // Telas de detalhe (library/class/action + id) seguem sem hero: a tela mostra o próprio header.
    return !/^\/(library|class|action)\/[^/]+/.test(url);
  });

  public toggleSideBar(): void {
    this.closedByButton.set(true);
    this.isCollapsed.set(true);
  }

  public onSiderMouseEnter(): void {
    this.closedByButton.set(false);
    this.isCollapsed.set(false);
  }

  public onSiderMouseLeave(): void {
    if (this.closedByButton()) {
      this.isCollapsed.set(true);
    }
  }
}
